import { computed, effectScope, readonly, ref, shallowRef } from "vue";
import { useLocalStorage } from "@vueuse/core";
import { buildQuantumSelectItems, itemMatchesQuery } from "#/doctor_finder/omnisearch-quantum-items";
import { createOmnisearchApi } from "#ce/apps/shared/api/omnisearch";
import { createChatterboxApi } from "#ce/apps/shared/api/chatterbox";

// The shared doctor-finder omnisearch data source. ONE instance per embed
// (keyed by uuid) fetches the three autocomplete endpoints exactly once and
// builds the `<sp-quantum-select>` item trees that every consumer reads:
//
//   - <sp-finder-omnisearch>     → the full omnisearch (`omnisearch` set)
//   - <sp-filter-app specialty>  → the skill facet     (`skill` set)
//   - <sp-filter-app provider>   → the name facet      (`provider` set)
//
// It is a plain module-level singleton — NOT a Pinia store — because each custom
// element boots its own Vue app (see custom_elements/framework/app.js), so a
// Pinia store would fetch once per element. ESM module identity guarantees a
// single shared instance, and Vue's `ref`/`computed` reactivity is global, so the
// shared state stays reactive across the independent element apps.

// The "skill" facet (results-bar specialty filter) covers clinical skills only.
// Provider/location/visit-type results are excluded — they belong to the
// omnisearch launcher.
const SKILL_SECTIONS = [
  "common_specialties",
  "specialties",
  "conditions",
  "symptoms",
  "treatments",
  "clinical_interests",
];

// The "provider" facet (results-bar name filter) is physicians + locations.
const PROVIDER_SECTIONS = ["physicians", "locations"];

// localStorage namespace for recent searches.
const recentStorageKey = (uuid) => `recent_${uuid}_doctor_searches`;

// Group label fallback when the embed config omits `recentGroupLabel`.
const DEFAULT_RECENT_GROUP_LABEL = "Recent";

// Matches a URL that is already absolute — a scheme (`http://`, `https://`),
// scheme-relative (`//host`), or an inline `data:` URI. Such an icon is used
// verbatim; only a relative path gets host-prefixed.
const ABSOLUTE_URL_PATTERN = /^(?:[a-z]+:)?\/\//i;

function isAbsoluteUrl(url) {
  return ABSOLUTE_URL_PATTERN.test(url) || /^data:/i.test(url);
}

// Dev-only warning so a server-side data-contract change (a renamed/added result
// type, an id with no lookup record) surfaces in development instead of silently
// dropping content. No-op in production builds.
function devWarn(...args) {
  if (import.meta.env?.DEV) {
    console.warn("[omnisearch-store]", ...args);
  }
}

const instances = new Map();

/**
 * Returns the shared store instance for an embed, creating it on first use.
 *
 * @param {String} [uuid] - the doctor-finder embed uuid. Defaults to a shared
 *   key so unmanaged/single-embed pages still get one instance.
 */
export function useOmnisearchStore(uuid = "default") {
  if (!instances.has(uuid)) {
    instances.set(uuid, createStore(uuid));
  }

  return instances.get(uuid);
}

/**
 * Tear down an embed's shared store: abort any in-flight load (and its retry
 * poll), invalidate the load generation so a late response can't write back,
 * clear the cached data, and drop the uuid entry so the instance is collectable.
 * Called from embed teardown (e.g. an orphaned/removed doctor-finder embed).
 *
 * @param {String} [uuid] - the doctor-finder embed uuid (defaults to the shared key).
 */
export function disposeOmnisearchStore(uuid = "default") {
  const store = instances.get(uuid);
  if (store) {
    store.dispose();
    instances.delete(uuid);
  }
}

// Test seam: drop cached instances so each spec starts clean.
export function __resetOmnisearchStores() {
  instances.clear();
}

function createStore(uuid) {
  // Owns the reactive effects (the localStorage `storage` listener, the computed
  // item trees) so `dispose()` can release them with `scope.stop()`. Detached
  // because the store is a module singleton, not created in a component setup.
  const scope = effectScope(true);

  const config = ref(null);
  const loading = ref(false);
  const error = ref(null);
  // shallowRef: the merged document is a large plain object we only ever replace
  // wholesale — no need for deep reactivity over every nested record.
  const rawData = shallowRef(null);

  let loadPromise = null;
  // Whether the memoized `loadPromise` was started with config in hand. A lazy
  // `load()` before `configure()` fetches without `dataParams`; `configure()`
  // uses this to discard and restart that unconfigured request.
  let loadedWithConfig = false;
  // Bumped per `doLoad()` so a superseded (unconfigured) request can't clobber
  // the configured one's result when it resolves later.
  let loadGeneration = 0;
  // Aborts the in-flight load's network calls when a restart supersedes it, so a
  // discarded fetch (and its retry poll) stops instead of finishing in the
  // background. The generation guard still protects state.
  let loadController = null;
  let chatterboxApi = null;

  // Recent searches are backed by a reactive localStorage ref so a write made
  // mid-session invalidates the `omnisearch` computed and the reopened list
  // reflects it (a plain synchronous read would stay cached on the stale value).
  const recentSearches = scope.run(() => useLocalStorage(recentStorageKey(uuid), []));

  /**
   * Supply the embed's fetch config + omnisearch options and arm the fetch.
   * Idempotent — the first call wins, so a second embed init or an early
   * component mount is a no-op.
   */
  function configure(options = {}) {
    if (config.value) {
      devWarn("configure() called on an already-configured store; ignoring (first call wins)");
      return;
    }

    // Normalize the snake_case alias the embed may send so consumers read one key.
    config.value = { ...options, ariaLabel: options.ariaLabel ?? options.aria_label };

    if (options.chatterboxEnabled) {
      chatterboxApi = createChatterboxApi();
    }

    // A facet can trigger the lazy `load()` before the embed hands us its config
    // (`configure` runs on `sp:elements:initialized`, after facets mount). That
    // request went out without `dataParams` — drop it so the configured params
    // (specialty_type, hierarchy, common specialties, search scope) take effect.
    // Abort the in-flight fetch so it stops polling; the generation guard in
    // `doLoad` also protects state if a response lands before the abort.
    if (loadPromise && !loadedWithConfig) {
      loadController?.abort();
      loadPromise = null;
    }

    load();
  }

  // Lazily fetch on first read even if `configure` hasn't run yet (e.g. a
  // results-only embed). Single-flight — and `configure` restarts the fetch if
  // this ran before the config arrived.
  function load() {
    if (!loadPromise) {
      loadedWithConfig = config.value != null;
      loadPromise = doLoad();
    }

    return loadPromise;
  }

  async function doLoad() {
    const generation = (loadGeneration += 1);
    const controller = new AbortController();
    loadController = controller;
    const { signal } = controller;
    loading.value = true;

    try {
      const api = createOmnisearchApi({ dataParams: config.value?.dataParams });
      const [data, lookup, filters] = await Promise.all([
        api.data({ signal }),
        api.lookup({ signal }),
        api.filters({ signal }),
      ]);
      // A request superseded by a reconfigured load must not overwrite its result.
      if (generation !== loadGeneration) {
        return;
      }
      rawData.value = mergeData(data, lookup, filters, config.value ?? {});
      error.value = null;
    } catch (e) {
      // One endpoint failing leaves its siblings in flight — `api.data` polls
      // with backoff, so abort the shared controller to tear them down.
      controller.abort();
      if (generation === loadGeneration) {
        error.value = e;
        // Don't memoize a failed load — drop the promise so the next `load()`
        // (or `configure`) retries instead of replaying the settled failure,
        // which would leave the finder empty until a full page reload.
        loadPromise = null;
      }
    } finally {
      if (generation === loadGeneration) {
        loading.value = false;
      }
    }
  }

  // Each consumer gets its own built item tree from the relevant raw sections —
  // `buildQuantumSelectItems` nests/dedupes per list, so building subsets
  // separately keeps each facet's parent/child structure correct.
  const skill = scope.run(() =>
    computed(() => buildQuantumSelectItems(collectSections(rawData.value, SKILL_SECTIONS))),
  );
  const provider = scope.run(() =>
    computed(() => buildQuantumSelectItems(collectSections(rawData.value, PROVIDER_SECTIONS))),
  );
  const omnisearch = scope.run(() =>
    computed(() => buildQuantumSelectItems(collectOmnisearch(rawData.value, config.value, recentSearches.value))),
  );

  function hasLocalMatch(query) {
    const needle = (query ?? "").toLowerCase().trim();
    if (!needle) {
      return false;
    }

    return omnisearch.value.searchItems.some((item) => itemMatchesQuery(item, needle));
  }

  // Resolve a typed query to a single suggested-filter node via the NLP service.
  // Returns null when the service yields no usable filters.
  async function fetchSuggestedFilter(query, { signal } = {}) {
    if (!chatterboxApi || !query) {
      return null;
    }

    const data = await chatterboxApi.suggest(query, { signal });
    return buildSuggestedFilterNode(query, data, config.value?.suggestedFilterGroupLabel);
  }

  // Stop and reset the store: abort the in-flight load, invalidate the current
  // generation so any in-flight poll/response can't write back, stop the reactive
  // scope (releasing the localStorage listener), and clear cached state. The
  // instance is dropped from the registry by `disposeOmnisearchStore`.
  function dispose() {
    loadController?.abort();
    loadController = null;
    loadGeneration += 1;
    loadPromise = null;
    loadedWithConfig = false;
    scope.stop();
    rawData.value = null;
    error.value = null;
    loading.value = false;
  }

  return {
    uuid,
    loading: readonly(loading),
    error: readonly(error),
    config: readonly(config),
    rawData: readonly(rawData),
    skill,
    provider,
    omnisearch,
    configure,
    load,
    hasLocalMatch,
    fetchSuggestedFilter,
    dispose,
  };
}

// --- data assembly -----------------------------------------------------------

// The filters a visit-type result applies. The availability filter carries `visit_types` unless
// the standalone Visit Type filter owns the param, which filters by modality without also
// requiring bookable availability.
function visitTypeFilters(value, config) {
  if (config?.visitTypeFilterOwnsParam) {
    return { visit_types: value };
  }

  return { availability: true, visit_types: value };
}

// Hydrate the physician/location id lists from the lookup table, and synthesise
// the visit-type / provider-type result items from the filters facet endpoint.
function mergeData(data, lookup, filters, config) {
  const merged = { ...(data ?? {}) };
  const href = lookup?.base?.href ?? "";

  merged.physicians = hydrateLookup(
    lookup,
    "physicians",
    data?.physician_ids,
    "Physician",
    href,
    config.defaultPhysicianIcon,
  );
  merged.locations = hydrateLookup(
    lookup,
    "locations",
    data?.location_ids,
    "Location",
    href,
    config.defaultLocationIcon,
  );

  merged.visit_types = (filters?.visit_types ?? [])
    .filter((visitType) => visitType.visible)
    .map((visitType) => ({
      text: visitType.text,
      filters: visitTypeFilters(visitType.value, config),
      type: "VisitType",
      group: config.visitTypeGroupLabel,
      visibility: "query",
    }));

  merged.provider_types = (filters?.provider_types ?? []).map((providerType) => ({
    text: providerType.text,
    filters: { provider_type: providerType.value },
    type: "ProviderType",
    group: config.providerTypeGroupLabel,
    visibility: "query",
  }));

  return merged;
}

// Pick the records referenced by `ids` from the id-keyed lookup map, layering on
// the shared `base` defaults (visibility/type/group), a host-prefixed icon (or
// the default placeholder), and the synthesised live URL.
function hydrateLookup(lookup, key, ids, type, href, defaultIcon) {
  const map = lookup?.[key] ?? {};
  const base = lookup?.base?.[key] ?? {};
  let dropped = 0;

  const items = (ids ?? [])
    .map((id) => {
      const record = map[id] ?? map[String(id)];
      if (!record) {
        dropped += 1;
        return null;
      }

      // base wins for visibility/type/group.
      const item = { ...record, ...base };
      item.url = `${href}/live_url?id=${id}&type=${type}`;
      item.icon = resolveIcon(record.icon, href, defaultIcon);
      return item;
    })
    .filter(Boolean);

  if (dropped) {
    devWarn(`${dropped} ${key} id(s) had no record in the lookup table and were dropped`);
  }

  return items;
}

// An absolute icon (`http(s)://`, `//host`, `data:`) is used as-is; a relative
// path is host-prefixed; a missing icon falls back to the default placeholder.
function resolveIcon(icon, href, defaultIcon) {
  if (!icon) {
    return defaultIcon;
  }

  return isAbsoluteUrl(icon) ? icon : `${href}${icon}`;
}

function collectSections(rawData, sections) {
  if (!rawData) {
    return [];
  }

  return sections.flatMap((section) => (Array.isArray(rawData[section]) ? rawData[section] : []));
}

// The full omnisearch list, concatenated in the configured result-type order,
// with saved searches (from config) and recent searches (from the reactive
// localStorage ref) injected at their slots.
function collectOmnisearch(rawData, config, recents) {
  if (!rawData) {
    return [];
  }

  const order = config?.resultTypeOrder ?? rawData.omnisearch_result_types ?? [];
  const list = [];

  for (const type of order) {
    if (type === "saved_searches") {
      if (Array.isArray(config?.savedSearches)) {
        list.push(...config.savedSearches);
      }
    } else if (type === "recent_searches") {
      list.push(...recentRawItems(recents, config));
    } else if (Array.isArray(rawData[type])) {
      list.push(...rawData[type]);
    } else {
      devWarn(`ordered result type "${type}" is neither saved/recent nor a present section — dropped`);
    }
  }

  return list;
}

// Shape the visitor's recent searches (the reactive localStorage array) as
// omnisearch raw items.
function recentRawItems(recents, config) {
  const group = config?.recentGroupLabel ?? DEFAULT_RECENT_GROUP_LABEL;

  return (Array.isArray(recents) ? recents : [])
    .filter((recent) => recent.url || recent.filters)
    .map((recent) => ({
      text: recent.text,
      url: recent.url,
      icon: recent.icon,
      filters: recent.filters,
      visibility: "no-query",
      group,
      navigateOnSelect: true,
      type: recent.type === "doctor" ? "Physician" : "Query",
    }));
}

// --- chatterbox --------------------------------------------------------------

// Turn the chatterbox response ({ filter: { text, value } }) into a single
// quantum-select node routed via `payload.filters`.
function buildSuggestedFilterNode(query, data, groupLabel) {
  const filterText = [];
  const filters = {};

  for (const [filter, filterData] of Object.entries(data ?? {})) {
    if (filterData?.text?.length) {
      filterText.push(filterData.text);
      filters[filter] = filterData.value;
    }
  }

  if (!filterText.length) {
    return null;
  }

  return {
    title: filterText.join(", "),
    value: `suggested-filter|${query}`,
    group: groupLabel ?? "Search with suggested filters",
    keywords: [query],
    payload: { filters, type: "suggested-filter", navigateOnSelect: true },
  };
}
